import { RequestHandler } from 'express';
import rateLimit from 'express-rate-limit';
import { HttpError } from './error-handler';

/**
 * Options for rate limiting middleware
 */
export interface RateLimiterOptions {
  /**
   * Time window in milliseconds
   * @default 900000 (15 minutes)
   */
  windowMs?: number;

  /**
   * Maximum number of requests per IP in the time window
   * @default 100
   */
  max?: number;

  /**
   * Whether to add standard rate limiting headers
   * @default true
   */
  standardHeaders?: boolean;

  /**
   * Message to send when rate limit is exceeded
   * @default 'Too many requests, please try again later.'
   */
  message?: string;

  /**
   * Skip rate limiting for certain requests (return true to skip)
   */
  skip?: (req: any) => boolean;

  /**
   * Additional options to pass to express-rate-limit
   */
  additionalOptions?: Record<string, any>;
}

/**
 * Create rate limiting middleware
 *
 * @param options Rate limiting options
 * @returns Express middleware
 */
export function rateLimiterMiddleware(options: RateLimiterOptions = {}): RequestHandler {
  const limiter = rateLimit({
    windowMs: options.windowMs || 15 * 60 * 1000, // Default: 15 minutes
    max: options.max || 100, // Default: limit each IP to 100 requests per windowMs
    standardHeaders: options.standardHeaders !== false, // Return rate limit info in the `RateLimit-*` headers
    legacyHeaders: false, // Disable the `X-RateLimit-*` headers
    skipFailedRequests: false, // Don't count failed requests
    skip: options.skip,

    // Custom handler for rate limit exceeded
    handler: (req, res, next, limitOptions) => {
      const error = new HttpError(
        options.message || 'Too many requests, please try again later.',
        429,
        'TOO_MANY_REQUESTS',
        {
          limit: limitOptions.max,
          windowMs: limitOptions.windowMs
        }
      );

      next(error);
    },

    // Apply any additional options
    ...(options.additionalOptions || {})
  });

  // Cast to RequestHandler to satisfy TypeScript
  return limiter as unknown as RequestHandler;
}
